<template>
    <div class="w-full">
        <div class="space-y-6">
            <div class="bg-white shadow px-4 py-5 sm:rounded sm:p-6">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $t("incidents.forms.affected_projects.title") }}
                        </h3>
                        <p class="mt-1 text-sm text-gray-500">
                            {{ $t("incidents.all_projects") }}
                        </p>
                        <p
                            class="
                                font-semibold
                                text-sm text-lightblue-500
                                hover:text-lightblue-600
                                underline
                                cursor-pointer
                            "
                            @click="showingEditAffectedProjectsModal = true"
                        >
                            {{ $t("incidents.buttons.edit") }}
                        </p>

                        <dialog-modal
                            :show="showingEditAffectedProjectsModal"
                            @close="showingEditAffectedProjectsModal = false"
                        >
                            <template #content>
                                <affected-project-selector
                                    :all-projects="allProjects"
                                    :affected-projects="incidentData.affectedProjects"
                                    @affected-projects="incidentData.affectedProjects = $event"
                                ></affected-project-selector>
                            </template>

                            <template #footer>
                                <button-primary @button-click="showingEditAffectedProjectsModal = false">
                                    {{ $t("incidents.buttons.ok") }}
                                </button-primary>
                            </template>
                        </dialog-modal>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="grid grid-cols-6 gap-6">
                            <div class="col-span-12">
                                <div v-if="affectedProjects.length === 0" class="flex">
                                    <p class="mx-auto text-gray-400">
                                        {{ $t("incidents.forms.affected_projects.empty") }}
                                    </p>
                                </div>

                                <div
                                    v-for="(project, index) in affectedProjects"
                                    :key="index"
                                    class="px-4 py-5 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6"
                                    :class="index % 2 ? 'bg-gray-50' : 'bg-gray-100'"
                                >
                                    <div class="text-sm font-medium sm:col-span-1 text-gray-500">
                                        {{ project.name }}
                                    </div>
                                    <div class="mt-1 text-sm sm:mt-0 sm:col-span-1 text-gray-900">
                                        {{ project.website_url }}
                                    </div>
                                </div>

                                <ul v-if="errors.affectedProjects">
                                    <li
                                        v-for="error in errors.affectedProjects"
                                        :key="error"
                                        class="text-red-500 text-xs"
                                    >
                                        {{ error }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white shadow px-4 py-5 sm:rounded sm:p-6">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $t("incidents.forms.project_checks.sensu.title") }}
                        </h3>
                        <p class="mt-1 text-sm text-gray-500">
                            {{ $t("incidents.all_project_checks") }}
                        </p>
                        <p
                            class="
                                font-semibold
                                text-sm text-lightblue-500
                                hover:text-lightblue-600
                                underline
                                cursor-pointer
                            "
                            @click="showingEditProjectChecksModal = true"
                        >
                            {{ $t("incidents.buttons.edit") }}
                        </p>

                        <dialog-modal
                            :show="showingEditProjectChecksModal"
                            @close="showingEditProjectChecksModal = false"
                        >
                            <template #content>
                                <create-sensu-intergration
                                    :pre-selected-events="projectChecksSensu"
                                    @sensu-project-check="setSensuProjectCheck"
                                ></create-sensu-intergration>
                            </template>

                            <template #footer>
                                <button-primary @button-click="showingEditProjectChecksModal = false">
                                    {{ $t("incidents.buttons.ok") }}
                                </button-primary>
                            </template>
                        </dialog-modal>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="grid grid-cols-6 gap-6">
                            <div class="col-span-12">
                                <div v-if="sensuProjectChecksReferences.length === 0" class="flex">
                                    <p class="mx-auto text-gray-400">
                                        {{ $t("incidents.forms.project_checks.sensu.empty") }}
                                    </p>
                                </div>

                                <div
                                    v-for="(event, index) in sensuProjectChecksReferences"
                                    :key="index"
                                    class="px-4 py-5 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6"
                                    :class="index % 2 ? 'bg-gray-50' : 'bg-gray-100'"
                                >
                                    <div class="text-sm font-medium sm:col-span-1 text-gray-500">
                                        {{ event.name ?? event.entity + " " + event.check }}
                                    </div>
                                    <div class="mt-1 text-sm sm:mt-0 sm:col-span-1 text-gray-900">
                                        {{ event.description ?? event["output"] }}
                                    </div>
                                </div>

                                <ul v-if="errors.affectedProjects">
                                    <li
                                        v-for="error in errors.affectedProjects"
                                        :key="error"
                                        class="text-red-500 text-xs"
                                    >
                                        {{ error }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white shadow px-4 py-5 sm:rounded sm:p-6">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $t("incidents.title") }}
                        </h3>
                        <p class="mt-1 text-sm text-gray-500">
                            {{ $t("incidents.forms.title.description") }}
                        </p>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="grid grid-cols-6 gap-6">
                            <div class="col-span-12">
                                <text-input
                                    v-model="incidentData.title"
                                    :label="$t('incidents.forms.title.title')"
                                    :errors="errors.title"
                                    :placeholder="$t('incidents.forms.title.placeholder')"
                                ></text-input>
                            </div>

                            <div class="col-span-12">
                                <text-area-input
                                    v-model="incidentData.description"
                                    :label="$t('incidents.forms.description.title')"
                                    :placeholder="$t('incidents.forms.description.placeholder')"
                                    :errors="errors.description"
                                    class="mt-2"
                                ></text-area-input>
                            </div>

                            <div class="col-span-12 md:col-span-3">
                                <label for="priority" class="block text-sm text-gray-800">
                                    {{ $t("incidents.forms.priority.title") }}
                                </label>
                                <select
                                    id="priority"
                                    v-model="incidentData.prio"
                                    class="
                                        mt-1
                                        block
                                        w-full
                                        py-2
                                        px-3
                                        border border-gray-300
                                        rounded-md
                                        shadow-sm
                                        focus:outline-none
                                        focus:ring-indigo-500
                                        focus:border-indigo-500
                                        sm:text-sm
                                    "
                                >
                                    <option value="Low">
                                        {{ $t("incidents.forms.priority.items.low") }}
                                    </option>
                                    <option value="Medium">
                                        {{ $t("incidents.forms.priority.items.medium") }}
                                    </option>
                                    <option value="High">
                                        {{ $t("incidents.forms.priority.items.high") }}
                                    </option>
                                </select>
                            </div>

                            <div class="col-span-12 md:col-span-3">
                                <label for="status" class="block text-sm text-gray-800">{{
                                    $t("incidents.forms.status.title")
                                }}</label>
                                <select
                                    id="status"
                                    v-model="incidentData.resolved"
                                    class="
                                        mt-1
                                        block
                                        w-full
                                        py-2
                                        px-3
                                        border border-gray-300
                                        rounded-md
                                        shadow-sm
                                        focus:outline-none
                                        focus:ring-indigo-500
                                        focus:border-indigo-500
                                        sm:text-sm
                                    "
                                >
                                    <option :value="false">
                                        {{ $t("incidents.forms.status.items.active") }}
                                    </option>
                                    <option :value="true">
                                        {{ $t("incidents.forms.status.items.fixed") }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white shadow px-4 py-5 sm:rounded sm:p-6">
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $t("incidents.statuses.title") }}
                        </h3>
                        <p class="mt-1 text-sm text-gray-500">
                            {{ $t("incidents.statuses.description") }}
                        </p>
                        <p
                            class="
                                font-semibold
                                text-sm text-lightblue-500
                                hover:text-lightblue-600
                                underline
                                cursor-pointer
                            "
                            @click="showingAddIncidentStatusModal = true"
                        >
                            {{ $t("incidents.buttons.add") }}
                        </p>

                        <dialog-modal
                            :show="showingAddIncidentStatusModal"
                            @close="showingAddIncidentStatusModal = false"
                        >
                            <template #title>
                                <h1 class="text-2xl text-gray-900">{{ $t("incidents.statuses.new.title") }}</h1>
                            </template>

                            <template #content>
                                <p class="text-gray-800 text-sm">
                                    {{ $t("incidents.statuses.new.forms.title.title") }}
                                </p>
                                <input
                                    v-model="newStatusMessage.title"
                                    type="text"
                                    class="
                                        w-full
                                        bg-gray-50
                                        rounded
                                        border border-gray-300
                                        focus:border-lightblue-500
                                        focus:outline-none
                                    "
                                    :class="{
                                        'border-red-500': newStatusMessage.title === '',
                                    }"
                                    :placeholder="$t('incidents.statuses.new.forms.title.placeholder')"
                                />

                                <p class="mt-2 text-gray-800 text-sm">
                                    {{ $t("incidents.forms.description.title") }}
                                </p>
                                <textarea
                                    v-model="newStatusMessage.description"
                                    class="
                                        w-full
                                        bg-gray-50
                                        rounded
                                        border border-gray-300
                                        focus:border-lightblue-500
                                        focus:outline-none
                                    "
                                    :class="{
                                        'border-red-500': newStatusMessage.description === '',
                                    }"
                                    style="height: 8rem"
                                ></textarea>

                                <div class="flex flex-wrap w-full">
                                    <div class="block w-full md:w-1/2">
                                        <p class="mt-2 text-gray-800 text-sm">
                                            {{ $t("incidents.statuses.new.forms.icon.title") }}
                                        </p>
                                        <div class="flex">
                                            <select
                                                v-model="newStatusMessage.icon"
                                                class="
                                                    w-full
                                                    bg-gray-50
                                                    rounded
                                                    border border-gray-300
                                                    focus:border-lightblue-500
                                                    focus:outline-none
                                                "
                                            >
                                                <option value="gear">
                                                    {{ $t("incidents.statuses.new.forms.icon.items.gear") }}
                                                </option>
                                                <option value="lightbulb">
                                                    {{ $t("incidents.statuses.new.forms.icon.items.lightbulb") }}
                                                </option>
                                                <option value="warning">
                                                    {{ $t("incidents.statuses.new.forms.icon.items.warning") }}
                                                </option>
                                                <option value="check">
                                                    {{ $t("incidents.statuses.new.forms.icon.items.check") }}
                                                </option>
                                            </select>

                                            <status-icon
                                                class="ml-2 my-auto flex-shrink-0"
                                                :icon="newStatusMessage.icon"
                                            ></status-icon>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <template #footer>
                                <div class="flex">
                                    <button-secondary button-style="danger" @button-click="cancelAddIncidentStatus">
                                        {{ $t("incidents.buttons.cancel") }}
                                    </button-secondary>

                                    <button-primary class="ml-auto" @button-click="addIncidentStatus">
                                        {{ $t("incidents.buttons.add") }}
                                    </button-primary>
                                </div>
                            </template>
                        </dialog-modal>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <dl>
                            <div
                                v-for="(status, index) in incidentData.timeline"
                                :key="index"
                                class="px-4 py-5 flex sm:px-6"
                                :class="index % 2 ? 'bg-gray-50' : 'bg-gray-100'"
                            >
                                <dt class="flex">
                                    <status-icon class="my-auto" :icon="status.icon"></status-icon>
                                </dt>
                                <dd class="flex flex-grow">
                                    <p
                                        class="mx-2 my-auto text-sm"
                                        :class="status.id ? 'text-gray-900' : 'text-green-600'"
                                    >
                                        {{ status.title }}
                                    </p>

                                    <p
                                        class="
                                            ml-auto
                                            my-auto
                                            font-semibold
                                            text-sm text-lightblue-500
                                            hover:text-lightblue-600
                                            underline
                                            cursor-pointer
                                        "
                                        @click="beginEditIncidentStatus(index)"
                                    >
                                        {{ $t("incidents.buttons.edit") }}
                                    </p>
                                </dd>
                            </div>

                            <dialog-modal
                                :show="editIncidentStatusIndex !== null"
                                @close="editIncidentStatusIndex = null"
                            >
                                <template #title>
                                    <h1 class="text-2xl text-gray-900">
                                        {{ $t("incidents.status.new.title_edit") }}
                                    </h1>
                                </template>

                                <template #content>
                                    <p class="text-gray-800 text-sm">
                                        {{ $t("incidents.statuses.new.forms.title.title") }}
                                    </p>
                                    <input
                                        v-model="editStatusMessage.title"
                                        type="text"
                                        class="
                                            w-full
                                            bg-gray-50
                                            rounded
                                            border border-gray-300
                                            focus:border-lightblue-500
                                            focus:outline-none
                                        "
                                        :class="{
                                            'border-red-500': editStatusMessage.title === '',
                                        }"
                                        :placeholder="$t('incidents.statuses.new.forms.title.placeholder')"
                                    />

                                    <p class="mt-2 text-gray-800 text-sm">
                                        {{ $t("incidents.forms.description.title") }}
                                    </p>
                                    <textarea
                                        v-model="editStatusMessage.description"
                                        class="
                                            w-full
                                            bg-gray-50
                                            rounded
                                            border border-gray-300
                                            focus:border-lightblue-500
                                            focus:outline-none
                                        "
                                        :class="{
                                            'border-red-500': editStatusMessage.description === '',
                                        }"
                                        style="height: 8rem"
                                    ></textarea>

                                    <div class="flex flex-wrap w-full">
                                        <div class="block w-full md:w-1/2">
                                            <p class="mt-2 text-gray-800 text-sm">
                                                {{ $t("incidents.statuses.new.forms.icon.title") }}
                                            </p>
                                            <div class="flex">
                                                <select
                                                    v-model="editStatusMessage.icon"
                                                    class="
                                                        w-full
                                                        bg-gray-50
                                                        rounded
                                                        border border-gray-300
                                                        focus:border-lightblue-500
                                                        focus:outline-none
                                                    "
                                                >
                                                    <option value="gear">
                                                        {{ $t("incidents.statuses.new.forms.icon.items.gear") }}
                                                    </option>
                                                    <option value="lightbulb">
                                                        {{ $t("incidents.statuses.new.forms.icon.items.lightbulb") }}
                                                    </option>
                                                    <option value="warning">
                                                        {{ $t("incidents.statuses.new.forms.icon.items.warning") }}
                                                    </option>
                                                    <option value="check">
                                                        {{ $t("incidents.statuses.new.forms.icon.items.check") }}
                                                    </option>
                                                </select>

                                                <status-icon
                                                    class="ml-2 my-auto flex-shrink-0"
                                                    :icon="editStatusMessage.icon"
                                                ></status-icon>
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <template #footer>
                                    <div class="flex">
                                        <button-secondary
                                            button-style="danger"
                                            @button-click="editIncidentStatusIndex = null"
                                        >
                                            {{ $t("incidents.buttons.cancel") }}
                                        </button-secondary>

                                        <button-primary class="ml-auto" @button-click="saveEditIncidentStatus">
                                            {{ $t("incidents.buttons.save") }}
                                        </button-primary>
                                    </div>
                                </template>
                            </dialog-modal>
                        </dl>
                    </div>
                </div>
            </div>

            <div class="flex justify-end pb-4">
                <button-primary :show-loading-indicator="isSubmitting" @button-click="saveChanges()">
                    {{ $t("incidents.buttons.save") }}
                </button-primary>
            </div>
        </div>
    </div>
</template>

<script>
import DialogModal from "@/Jetstream/DialogModal";
import ButtonSecondary from "@/Components/ButtonSecondary";
import AffectedProjectSelector from "@/Components/AffectedProjectSelector";
import CreateSensuIntergration from "@/Components/CreateSensuIntergration";
import ButtonPrimary from "@/Components/ButtonPrimary";
import StatusIcon from "@/Components/StatusIcon";
import { computed, reactive, ref } from "vue";
import { Inertia } from "@inertiajs/inertia";
import TextInput from "./TextInput";
import TextAreaInput from "./TextAreaInput";

export default {
    name: "EditIncident",
    components: {
        TextAreaInput,
        TextInput,
        StatusIcon,
        ButtonPrimary,
        ButtonSecondary,
        DialogModal,
        AffectedProjectSelector,
        CreateSensuIntergration,
    },
    props: {
        incident: {
            type: Object,
            required: true,
        },
        allProjects: {
            type: Array,
            required: true,
        },
        errorBags: {
            type: Object,
        },
    },
    setup(props) {
        const showingEditAffectedProjectsModal = ref(false);
        const showingAddIncidentStatusModal = ref(false);
        const showingEditProjectChecksModal = ref(false);
        const editIncidentStatusIndex = ref(null);
        const isSubmitting = ref(false);
        const projectChecksSensu = ref(props.incident.project_checks.sensu);

        const incidentData = reactive({
            title: props.incident.title,
            description: props.incident.description,
            resolved: props.incident.resolved,
            prio: props.incident.priority,
            affectedProjects: props.incident.affected_projects.map((project) => project.project_id),
            projectChecks: {
                sensu: projectChecksSensu.value,
                uptimeRobot: null,
            },
            timeline: props.incident.timeline,
        });

        const editStatusMessage = reactive({
            icon: "gear",
            title: "",
            description: "",
        });

        const newStatusMessage = reactive({
            icon: "gear",
            title: "",
            description: "",
        });

        const affectedProjects = computed(() =>
            props.allProjects.filter((project) => incidentData.affectedProjects.includes(project.id))
        );

        const sensuProjectChecksReferences = computed(() => {
            const noDuplicateChecks = ref([]);
            const sensuChecks = incidentData.projectChecks.sensu.map((check) => {
                if (check.checkable_references) return check.checkable_references;
                return check.id;
            });
            sensuChecks.forEach((check, index) => {
                if (sensuChecks.indexOf(check) === index) {
                    noDuplicateChecks.value.push(incidentData.projectChecks.sensu[index]);
                }
            });
            return noDuplicateChecks.value;
        });

        const clearAddIncidentStatusForm = () => {
            newStatusMessage.icon = "gear";
            newStatusMessage.title = "";
            newStatusMessage.description = "";
        };

        const errors = computed(() => (props.errorBags.default ? props.errorBags.default : {}));

        const addIncidentStatus = () => {
            showingAddIncidentStatusModal.value = false;
            incidentData.timeline.push({
                id: null,
                icon: newStatusMessage.icon,
                title: newStatusMessage.title,
                description: newStatusMessage.description,
            });
            clearAddIncidentStatusForm();
        };

        const cancelAddIncidentStatus = () => {
            clearAddIncidentStatusForm();
            showingAddIncidentStatusModal.value = false;
        };

        const beginEditIncidentStatus = (index) => {
            const status = incidentData.timeline[index];

            editStatusMessage.icon = status.icon;
            editStatusMessage.title = status.title;
            editStatusMessage.description = status.description;
            editIncidentStatusIndex.value = index;
        };

        const saveEditIncidentStatus = () => {
            const index = editIncidentStatusIndex.value;

            incidentData.timeline[index].icon = editStatusMessage.icon;
            incidentData.timeline[index].title = editStatusMessage.title;
            incidentData.timeline[index].description = editStatusMessage.description;
            editIncidentStatusIndex.value = null;
        };

        const setSensuProjectCheck = (sensuEvents) => {
            incidentData.projectChecks.sensu = sensuEvents;
        };

        const saveChanges = () => {
            Inertia.put(route("universal.incidents.update", props.incident.id), incidentData, {
                onStart: () => {
                    isSubmitting.value = true;
                },
                onFinish: () => {
                    isSubmitting.value = false;
                },
            });
        };

        return {
            showingEditAffectedProjectsModal,
            showingAddIncidentStatusModal,
            showingEditProjectChecksModal,
            editIncidentStatusIndex,
            incidentData,
            editStatusMessage,
            newStatusMessage,
            affectedProjects,
            errors,
            isSubmitting,
            projectChecksSensu,
            sensuProjectChecksReferences,
            cancelAddIncidentStatus,
            addIncidentStatus,
            saveEditIncidentStatus,
            beginEditIncidentStatus,
            setSensuProjectCheck,
            saveChanges,
        };
    },
};
</script>
